import { useState, useEffect } from 'react';
import { clsx } from 'clsx';

// Support Plan data structure - proactive wellness focus
interface SupportPlanData {
  // Schedules
  checkInSchedule: CheckInSchedule;
  reminderSchedule: ReminderSchedule;
  // Wellness tracking
  thingsToWatch: string[];
  copingStrategies: string[];
  selfCareRoutines: string[];
  healthyDistractions: SocialContact[];
  supportNetwork: SupportContact[];
  professionalSupport: ProfessionalContact[];
  medications: Medication[];
  motivations: string[];
  // Check-in history
  recentCheckIns: CheckInEntry[];
}

interface CheckInSchedule {
  enabled: boolean;
  frequency: 'daily' | 'twice_daily' | 'weekly' | 'custom';
  times: string[]; // HH:MM format
  daysOfWeek: number[]; // 0-6, Sunday = 0
}

interface ReminderSchedule {
  enabled: boolean;
  medicationReminders: boolean;
  selfCareReminders: boolean;
  times: string[];
}

interface CheckInEntry {
  id: string;
  timestamp: number;
  mood: number; // 1-5
  anxiety: number; // 1-5
  energy: number; // 1-5
  impulsivity: number; // 1-5
  sleep: number; // 1-5
  notes?: string;
}

interface SocialContact {
  id: string;
  name: string;
  place?: string;
  notes?: string;
}

interface SupportContact {
  id: string;
  name: string;
  relationship: string;
  phone?: string;
}

interface ProfessionalContact {
  id: string;
  name: string;
  role: string;
  phone: string;
  emergencyPhone?: string;
}

interface Medication {
  id: string;
  name: string;
  dosage: string;
  frequency: string;
  purpose?: string;
  prescriber?: string;
}

type Section =
  | 'schedules'
  | 'things-to-watch'
  | 'coping-strategies'
  | 'self-care'
  | 'healthy-distractions'
  | 'support-network'
  | 'professional-support'
  | 'medications'
  | 'motivations';

const SECTIONS: { id: Section; title: string; emoji: string; description: string }[] = [
  {
    id: 'schedules',
    title: 'Check-ins & Reminders',
    emoji: '🔔',
    description: 'Set your notification and check-in schedule',
  },
  {
    id: 'things-to-watch',
    title: 'Things to Watch For',
    emoji: '👁️',
    description: 'Patterns that signal I need extra support',
  },
  {
    id: 'coping-strategies',
    title: 'Coping Strategies',
    emoji: '🧘',
    description: 'What helps when I\'m feeling off',
  },
  {
    id: 'self-care',
    title: 'Self-Care Routines',
    emoji: '🌱',
    description: 'Daily habits that keep me grounded',
  },
  {
    id: 'healthy-distractions',
    title: 'Healthy Distractions',
    emoji: '🎯',
    description: 'People and activities that help me reset',
  },
  {
    id: 'support-network',
    title: 'Support Network',
    emoji: '💬',
    description: 'People I can reach out to',
  },
  {
    id: 'professional-support',
    title: 'Professional Support',
    emoji: '👨‍⚕️',
    description: 'Therapists, doctors, and crisis resources',
  },
  {
    id: 'medications',
    title: 'Medications',
    emoji: '💊',
    description: 'My prescriptions and supplements',
  },
  {
    id: 'motivations',
    title: 'What Matters to Me',
    emoji: '💝',
    description: 'My goals, values, and priorities',
  },
];

const STORAGE_KEY = 'wholeworm-support-plan';

const DEFAULT_DATA: SupportPlanData = {
  checkInSchedule: {
    enabled: false,
    frequency: 'daily',
    times: ['09:00', '21:00'],
    daysOfWeek: [0, 1, 2, 3, 4, 5, 6],
  },
  reminderSchedule: {
    enabled: false,
    medicationReminders: true,
    selfCareReminders: true,
    times: ['08:00', '20:00'],
  },
  thingsToWatch: [],
  copingStrategies: [],
  selfCareRoutines: [],
  healthyDistractions: [],
  supportNetwork: [],
  professionalSupport: [],
  medications: [],
  motivations: [],
  recentCheckIns: [],
};

function generateId(): string {
  return Math.random().toString(36).substring(2, 9);
}

export function SafetyPlan() {
  const [data, setData] = useState<SupportPlanData>(DEFAULT_DATA);
  const [expandedSection, setExpandedSection] = useState<Section | null>(null);
  const [newItemText, setNewItemText] = useState('');
  const [showCheckIn, setShowCheckIn] = useState(false);
  const [checkInData, setCheckInData] = useState({
    mood: 3,
    anxiety: 3,
    energy: 3,
    impulsivity: 1,
    sleep: 3,
    notes: '',
  });

  // Load from localStorage
  useEffect(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        setData({ ...DEFAULT_DATA, ...parsed });
      } catch (e) {
        console.error('Failed to load support plan:', e);
      }
    }
  }, []);

  // Save to localStorage
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  }, [data]);

  const toggleSection = (section: Section) => {
    setExpandedSection(expandedSection === section ? null : section);
    setNewItemText('');
  };

  // Simple string array handlers
  const addStringItem = (key: keyof Pick<SupportPlanData, 'thingsToWatch' | 'copingStrategies' | 'selfCareRoutines' | 'motivations'>) => {
    if (!newItemText.trim()) return;
    setData(prev => ({
      ...prev,
      [key]: [...prev[key], newItemText.trim()],
    }));
    setNewItemText('');
  };

  const removeStringItem = (key: keyof Pick<SupportPlanData, 'thingsToWatch' | 'copingStrategies' | 'selfCareRoutines' | 'motivations'>, index: number) => {
    setData(prev => ({
      ...prev,
      [key]: prev[key].filter((_, i) => i !== index),
    }));
  };

  // Support network handlers
  const addSupportContact = () => {
    const name = newItemText.trim();
    if (!name) return;
    setData(prev => ({
      ...prev,
      supportNetwork: [...prev.supportNetwork, { id: generateId(), name, relationship: '', phone: '' }],
    }));
    setNewItemText('');
  };

  const updateSupportContact = (id: string, updates: Partial<SupportContact>) => {
    setData(prev => ({
      ...prev,
      supportNetwork: prev.supportNetwork.map(c => c.id === id ? { ...c, ...updates } : c),
    }));
  };

  const removeSupportContact = (id: string) => {
    setData(prev => ({
      ...prev,
      supportNetwork: prev.supportNetwork.filter(c => c.id !== id),
    }));
  };

  // Professional support handlers
  const addProfessionalContact = () => {
    const name = newItemText.trim();
    if (!name) return;
    setData(prev => ({
      ...prev,
      professionalSupport: [...prev.professionalSupport, { id: generateId(), name, role: '', phone: '' }],
    }));
    setNewItemText('');
  };

  const updateProfessionalContact = (id: string, updates: Partial<ProfessionalContact>) => {
    setData(prev => ({
      ...prev,
      professionalSupport: prev.professionalSupport.map(c => c.id === id ? { ...c, ...updates } : c),
    }));
  };

  const removeProfessionalContact = (id: string) => {
    setData(prev => ({
      ...prev,
      professionalSupport: prev.professionalSupport.filter(c => c.id !== id),
    }));
  };

  // Healthy distractions handlers
  const addHealthyDistraction = () => {
    const name = newItemText.trim();
    if (!name) return;
    setData(prev => ({
      ...prev,
      healthyDistractions: [...prev.healthyDistractions, { id: generateId(), name }],
    }));
    setNewItemText('');
  };

  const updateHealthyDistraction = (id: string, updates: Partial<SocialContact>) => {
    setData(prev => ({
      ...prev,
      healthyDistractions: prev.healthyDistractions.map(c => c.id === id ? { ...c, ...updates } : c),
    }));
  };

  const removeHealthyDistraction = (id: string) => {
    setData(prev => ({
      ...prev,
      healthyDistractions: prev.healthyDistractions.filter(c => c.id !== id),
    }));
  };

  // Medication handlers
  const addMedication = () => {
    const name = newItemText.trim();
    if (!name) return;
    setData(prev => ({
      ...prev,
      medications: [...prev.medications, { id: generateId(), name, dosage: '', frequency: '' }],
    }));
    setNewItemText('');
  };

  const updateMedication = (id: string, updates: Partial<Medication>) => {
    setData(prev => ({
      ...prev,
      medications: prev.medications.map(m => m.id === id ? { ...m, ...updates } : m),
    }));
  };

  const removeMedication = (id: string) => {
    setData(prev => ({
      ...prev,
      medications: prev.medications.filter(m => m.id !== id),
    }));
  };

  // Check-in handlers
  const submitCheckIn = () => {
    const entry: CheckInEntry = {
      id: generateId(),
      timestamp: Date.now(),
      ...checkInData,
    };
    setData(prev => ({
      ...prev,
      recentCheckIns: [entry, ...prev.recentCheckIns].slice(0, 30), // Keep last 30
    }));
    setShowCheckIn(false);
    setCheckInData({ mood: 3, anxiety: 3, energy: 3, impulsivity: 1, sleep: 3, notes: '' });
  };

  const getSectionCount = (section: Section): number => {
    switch (section) {
      case 'schedules': return data.checkInSchedule.enabled || data.reminderSchedule.enabled ? 1 : 0;
      case 'things-to-watch': return data.thingsToWatch.length;
      case 'coping-strategies': return data.copingStrategies.length;
      case 'self-care': return data.selfCareRoutines.length;
      case 'healthy-distractions': return data.healthyDistractions.length;
      case 'support-network': return data.supportNetwork.length;
      case 'professional-support': return data.professionalSupport.length;
      case 'medications': return data.medications.length;
      case 'motivations': return data.motivations.length;
      default: return 0;
    }
  };

  // Get display items for collapsed view
  const getItemsForSection = (section: Section): string[] => {
    switch (section) {
      case 'schedules':
        const items = [];
        if (data.checkInSchedule.enabled) items.push(`Check-ins: ${data.checkInSchedule.frequency}`);
        if (data.reminderSchedule.enabled) items.push(`Reminders: ${data.reminderSchedule.times.join(', ')}`);
        return items;
      case 'things-to-watch': return data.thingsToWatch;
      case 'coping-strategies': return data.copingStrategies;
      case 'self-care': return data.selfCareRoutines;
      case 'healthy-distractions': return data.healthyDistractions.map(c => c.name + (c.notes ? ` - ${c.notes}` : ''));
      case 'support-network': return data.supportNetwork.map(c => `${c.name}${c.relationship ? ` (${c.relationship})` : ''}`);
      case 'professional-support': return data.professionalSupport.map(c => `${c.name}${c.role ? ` - ${c.role}` : ''}`);
      case 'medications': return data.medications.map(m => `${m.name}${m.dosage ? ` ${m.dosage}` : ''}`);
      case 'motivations': return data.motivations;
      default: return [];
    }
  };

  // Render items in collapsed view
  const renderCollapsedItems = (section: Section) => {
    const items = getItemsForSection(section);
    if (items.length === 0) return null;

    return (
      <div className="mt-3 space-y-1">
        {items.slice(0, 3).map((item, index) => (
          <div key={index} className="flex items-center gap-2 text-sm text-white/70">
            <span className="text-white/30">•</span>
            <span className="truncate">{item}</span>
          </div>
        ))}
        {items.length > 3 && (
          <p className="text-xs text-white/40 pl-4">+{items.length - 3} more</p>
        )}
      </div>
    );
  };

  const renderStringList = (
    items: string[],
    key: keyof Pick<SupportPlanData, 'thingsToWatch' | 'copingStrategies' | 'selfCareRoutines' | 'motivations'>,
    placeholder: string
  ) => (
    <div className="space-y-2">
      {items.map((item, index) => (
        <div key={index} className="flex items-center gap-2 bg-white/5 rounded-lg p-3">
          <span className="flex-1 text-sm">{item}</span>
          <button
            onClick={() => removeStringItem(key, index)}
            className="text-white/30 hover:text-accent-danger text-sm px-2"
            aria-label="Remove item"
          >
            ×
          </button>
        </div>
      ))}
      <div className="flex gap-2">
        <input
          type="text"
          value={newItemText}
          onChange={(e) => setNewItemText(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && addStringItem(key)}
          placeholder={placeholder}
          className="flex-1 bg-white/10 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-accent-primary"
        />
        <button
          onClick={() => addStringItem(key)}
          disabled={!newItemText.trim()}
          className="btn-primary px-4 py-2 text-sm"
        >
          Add
        </button>
      </div>
    </div>
  );

  const renderSchedules = () => (
    <div className="space-y-4">
      {/* Check-in Schedule */}
      <div className="bg-white/5 rounded-lg p-4 space-y-3">
        <div className="flex items-center justify-between">
          <div>
            <h4 className="font-medium">Check-in Reminders</h4>
            <p className="text-xs text-white/40">Get prompted to log how you're feeling</p>
          </div>
          <button
            onClick={() => setData(prev => ({
              ...prev,
              checkInSchedule: { ...prev.checkInSchedule, enabled: !prev.checkInSchedule.enabled }
            }))}
            className={clsx(
              'w-12 h-7 rounded-full transition-colors relative',
              data.checkInSchedule.enabled ? 'bg-accent-primary' : 'bg-white/20'
            )}
          >
            <span className={clsx(
              'absolute top-1 w-5 h-5 rounded-full bg-white transition-transform',
              data.checkInSchedule.enabled ? 'left-6' : 'left-1'
            )} />
          </button>
        </div>

        {data.checkInSchedule.enabled && (
          <>
            <div className="grid grid-cols-4 gap-2">
              {(['daily', 'twice_daily', 'weekly', 'custom'] as const).map((freq) => (
                <button
                  key={freq}
                  onClick={() => setData(prev => ({
                    ...prev,
                    checkInSchedule: { ...prev.checkInSchedule, frequency: freq }
                  }))}
                  className={clsx(
                    'py-2 px-2 rounded-lg text-xs font-medium transition-colors',
                    data.checkInSchedule.frequency === freq
                      ? 'bg-accent-primary text-white'
                      : 'bg-white/10 text-white/70'
                  )}
                >
                  {freq === 'twice_daily' ? '2x Daily' : freq.charAt(0).toUpperCase() + freq.slice(1)}
                </button>
              ))}
            </div>

            <div className="flex flex-wrap gap-2">
              {data.checkInSchedule.times.map((time, i) => (
                <div key={i} className="flex items-center gap-1 bg-white/10 rounded px-2 py-1">
                  <input
                    type="time"
                    value={time}
                    onChange={(e) => {
                      const newTimes = [...data.checkInSchedule.times];
                      newTimes[i] = e.target.value;
                      setData(prev => ({
                        ...prev,
                        checkInSchedule: { ...prev.checkInSchedule, times: newTimes }
                      }));
                    }}
                    className="bg-transparent text-sm focus:outline-none"
                  />
                  {data.checkInSchedule.times.length > 1 && (
                    <button
                      onClick={() => {
                        const newTimes = data.checkInSchedule.times.filter((_, idx) => idx !== i);
                        setData(prev => ({
                          ...prev,
                          checkInSchedule: { ...prev.checkInSchedule, times: newTimes }
                        }));
                      }}
                      className="text-white/30 hover:text-accent-danger"
                    >
                      ×
                    </button>
                  )}
                </div>
              ))}
              <button
                onClick={() => setData(prev => ({
                  ...prev,
                  checkInSchedule: { ...prev.checkInSchedule, times: [...prev.checkInSchedule.times, '12:00'] }
                }))}
                className="text-accent-primary text-sm px-2 py-1"
              >
                + Add time
              </button>
            </div>
          </>
        )}
      </div>

      {/* Reminder Schedule */}
      <div className="bg-white/5 rounded-lg p-4 space-y-3">
        <div className="flex items-center justify-between">
          <div>
            <h4 className="font-medium">Self-Care Reminders</h4>
            <p className="text-xs text-white/40">Get reminded about medications and routines</p>
          </div>
          <button
            onClick={() => setData(prev => ({
              ...prev,
              reminderSchedule: { ...prev.reminderSchedule, enabled: !prev.reminderSchedule.enabled }
            }))}
            className={clsx(
              'w-12 h-7 rounded-full transition-colors relative',
              data.reminderSchedule.enabled ? 'bg-accent-primary' : 'bg-white/20'
            )}
          >
            <span className={clsx(
              'absolute top-1 w-5 h-5 rounded-full bg-white transition-transform',
              data.reminderSchedule.enabled ? 'left-6' : 'left-1'
            )} />
          </button>
        </div>

        {data.reminderSchedule.enabled && (
          <>
            <div className="space-y-2">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={data.reminderSchedule.medicationReminders}
                  onChange={(e) => setData(prev => ({
                    ...prev,
                    reminderSchedule: { ...prev.reminderSchedule, medicationReminders: e.target.checked }
                  }))}
                  className="rounded"
                />
                <span className="text-sm">Medication reminders</span>
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={data.reminderSchedule.selfCareReminders}
                  onChange={(e) => setData(prev => ({
                    ...prev,
                    reminderSchedule: { ...prev.reminderSchedule, selfCareReminders: e.target.checked }
                  }))}
                  className="rounded"
                />
                <span className="text-sm">Self-care routine reminders</span>
              </label>
            </div>

            <div className="flex flex-wrap gap-2">
              {data.reminderSchedule.times.map((time, i) => (
                <div key={i} className="flex items-center gap-1 bg-white/10 rounded px-2 py-1">
                  <input
                    type="time"
                    value={time}
                    onChange={(e) => {
                      const newTimes = [...data.reminderSchedule.times];
                      newTimes[i] = e.target.value;
                      setData(prev => ({
                        ...prev,
                        reminderSchedule: { ...prev.reminderSchedule, times: newTimes }
                      }));
                    }}
                    className="bg-transparent text-sm focus:outline-none"
                  />
                  {data.reminderSchedule.times.length > 1 && (
                    <button
                      onClick={() => {
                        const newTimes = data.reminderSchedule.times.filter((_, idx) => idx !== i);
                        setData(prev => ({
                          ...prev,
                          reminderSchedule: { ...prev.reminderSchedule, times: newTimes }
                        }));
                      }}
                      className="text-white/30 hover:text-accent-danger"
                    >
                      ×
                    </button>
                  )}
                </div>
              ))}
              <button
                onClick={() => setData(prev => ({
                  ...prev,
                  reminderSchedule: { ...prev.reminderSchedule, times: [...prev.reminderSchedule.times, '12:00'] }
                }))}
                className="text-accent-primary text-sm px-2 py-1"
              >
                + Add time
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );

  const renderSupportNetwork = () => (
    <div className="space-y-3">
      {data.supportNetwork.map((contact) => (
        <div key={contact.id} className="bg-white/5 rounded-lg p-3 space-y-2">
          <div className="flex items-center justify-between">
            <input
              type="text"
              value={contact.name}
              onChange={(e) => updateSupportContact(contact.id, { name: e.target.value })}
              placeholder="Name"
              className="flex-1 bg-transparent font-medium focus:outline-none"
            />
            <button
              onClick={() => removeSupportContact(contact.id)}
              className="text-white/30 hover:text-accent-danger text-sm px-2"
            >
              ×
            </button>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <input
              type="text"
              value={contact.relationship}
              onChange={(e) => updateSupportContact(contact.id, { relationship: e.target.value })}
              placeholder="Relationship"
              className="bg-white/10 rounded px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-accent-primary"
            />
            <input
              type="tel"
              value={contact.phone || ''}
              onChange={(e) => updateSupportContact(contact.id, { phone: e.target.value })}
              placeholder="Phone"
              className="bg-white/10 rounded px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-accent-primary"
            />
          </div>
          {contact.phone && (
            <a
              href={`tel:${contact.phone}`}
              className="inline-flex items-center gap-1 text-accent-primary text-sm"
            >
              📞 Call {contact.name.split(' ')[0]}
            </a>
          )}
        </div>
      ))}
      <div className="flex gap-2">
        <input
          type="text"
          value={newItemText}
          onChange={(e) => setNewItemText(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && addSupportContact()}
          placeholder="Add contact name..."
          className="flex-1 bg-white/10 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-accent-primary"
        />
        <button
          onClick={addSupportContact}
          disabled={!newItemText.trim()}
          className="btn-primary px-4 py-2 text-sm"
        >
          Add
        </button>
      </div>
    </div>
  );

  const renderProfessionalSupport = () => (
    <div className="space-y-3">
      {/* Crisis resources - collapsible, not prominent */}
      <details className="bg-white/5 rounded-lg">
        <summary className="p-3 text-sm text-white/60 cursor-pointer hover:text-white/80">
          Crisis hotlines (if needed)
        </summary>
        <div className="px-3 pb-3 space-y-2 text-sm">
          <a href="tel:988" className="flex items-center justify-between">
            <span>988 Suicide & Crisis Lifeline</span>
            <span className="text-accent-primary">📞 988</span>
          </a>
          <a href="tel:1-833-456-4566" className="flex items-center justify-between">
            <span>Canada Crisis Line</span>
            <span className="text-accent-primary">📞 1-833-456-4566</span>
          </a>
          <a href="sms:741741" className="flex items-center justify-between">
            <span>Crisis Text Line</span>
            <span className="text-accent-primary">💬 HOME to 741741</span>
          </a>
        </div>
      </details>

      {data.professionalSupport.map((contact) => (
        <div key={contact.id} className="bg-white/5 rounded-lg p-3 space-y-2">
          <div className="flex items-center justify-between">
            <input
              type="text"
              value={contact.name}
              onChange={(e) => updateProfessionalContact(contact.id, { name: e.target.value })}
              placeholder="Name or Practice"
              className="flex-1 bg-transparent font-medium focus:outline-none"
            />
            <button
              onClick={() => removeProfessionalContact(contact.id)}
              className="text-white/30 hover:text-accent-danger text-sm px-2"
            >
              ×
            </button>
          </div>
          <input
            type="text"
            value={contact.role}
            onChange={(e) => updateProfessionalContact(contact.id, { role: e.target.value })}
            placeholder="Role (e.g., Therapist, Doctor)"
            className="w-full bg-white/10 rounded px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-accent-primary"
          />
          <div className="grid grid-cols-2 gap-2">
            <input
              type="tel"
              value={contact.phone}
              onChange={(e) => updateProfessionalContact(contact.id, { phone: e.target.value })}
              placeholder="Phone"
              className="bg-white/10 rounded px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-accent-primary"
            />
            <input
              type="tel"
              value={contact.emergencyPhone || ''}
              onChange={(e) => updateProfessionalContact(contact.id, { emergencyPhone: e.target.value })}
              placeholder="After-hours #"
              className="bg-white/10 rounded px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-accent-primary"
            />
          </div>
          {contact.phone && (
            <a
              href={`tel:${contact.phone}`}
              className="inline-flex items-center gap-1 text-accent-primary text-sm"
            >
              📞 Call
            </a>
          )}
        </div>
      ))}
      <div className="flex gap-2">
        <input
          type="text"
          value={newItemText}
          onChange={(e) => setNewItemText(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && addProfessionalContact()}
          placeholder="Add therapist, doctor..."
          className="flex-1 bg-white/10 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-accent-primary"
        />
        <button
          onClick={addProfessionalContact}
          disabled={!newItemText.trim()}
          className="btn-primary px-4 py-2 text-sm"
        >
          Add
        </button>
      </div>
    </div>
  );

  const renderHealthyDistractions = () => (
    <div className="space-y-3">
      {data.healthyDistractions.map((item) => (
        <div key={item.id} className="bg-white/5 rounded-lg p-3 space-y-2">
          <div className="flex items-center justify-between">
            <input
              type="text"
              value={item.name}
              onChange={(e) => updateHealthyDistraction(item.id, { name: e.target.value })}
              placeholder="Activity or person"
              className="flex-1 bg-transparent font-medium focus:outline-none"
            />
            <button
              onClick={() => removeHealthyDistraction(item.id)}
              className="text-white/30 hover:text-accent-danger text-sm px-2"
            >
              ×
            </button>
          </div>
          <input
            type="text"
            value={item.notes || ''}
            onChange={(e) => updateHealthyDistraction(item.id, { notes: e.target.value })}
            placeholder="Notes (optional)"
            className="w-full bg-white/10 rounded px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-accent-primary"
          />
        </div>
      ))}
      <div className="flex gap-2">
        <input
          type="text"
          value={newItemText}
          onChange={(e) => setNewItemText(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && addHealthyDistraction()}
          placeholder="Add activity or person..."
          className="flex-1 bg-white/10 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-accent-primary"
        />
        <button
          onClick={addHealthyDistraction}
          disabled={!newItemText.trim()}
          className="btn-primary px-4 py-2 text-sm"
        >
          Add
        </button>
      </div>
    </div>
  );

  const renderMedications = () => (
    <div className="space-y-3">
      {data.medications.map((med) => (
        <div key={med.id} className="bg-white/5 rounded-lg p-3 space-y-2">
          <div className="flex items-center justify-between">
            <input
              type="text"
              value={med.name}
              onChange={(e) => updateMedication(med.id, { name: e.target.value })}
              placeholder="Medication name"
              className="flex-1 bg-transparent font-medium focus:outline-none"
            />
            <button
              onClick={() => removeMedication(med.id)}
              className="text-white/30 hover:text-accent-danger text-sm px-2"
            >
              ×
            </button>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <input
              type="text"
              value={med.dosage}
              onChange={(e) => updateMedication(med.id, { dosage: e.target.value })}
              placeholder="Dosage"
              className="bg-white/10 rounded px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-accent-primary"
            />
            <input
              type="text"
              value={med.frequency}
              onChange={(e) => updateMedication(med.id, { frequency: e.target.value })}
              placeholder="Frequency"
              className="bg-white/10 rounded px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-accent-primary"
            />
          </div>
          <input
            type="text"
            value={med.purpose || ''}
            onChange={(e) => updateMedication(med.id, { purpose: e.target.value })}
            placeholder="Purpose (optional)"
            className="w-full bg-white/10 rounded px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-accent-primary"
          />
        </div>
      ))}
      <div className="flex gap-2">
        <input
          type="text"
          value={newItemText}
          onChange={(e) => setNewItemText(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && addMedication()}
          placeholder="Add medication..."
          className="flex-1 bg-white/10 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-accent-primary"
        />
        <button
          onClick={addMedication}
          disabled={!newItemText.trim()}
          className="btn-primary px-4 py-2 text-sm"
        >
          Add
        </button>
      </div>
    </div>
  );

  const renderSectionContent = (section: Section) => {
    switch (section) {
      case 'schedules':
        return renderSchedules();
      case 'things-to-watch':
        return renderStringList(data.thingsToWatch, 'thingsToWatch', 'e.g., Skipping meals, isolating...');
      case 'coping-strategies':
        return renderStringList(data.copingStrategies, 'copingStrategies', 'e.g., Deep breathing, call a friend...');
      case 'self-care':
        return renderStringList(data.selfCareRoutines, 'selfCareRoutines', 'e.g., Morning walk, journaling...');
      case 'healthy-distractions':
        return renderHealthyDistractions();
      case 'support-network':
        return renderSupportNetwork();
      case 'professional-support':
        return renderProfessionalSupport();
      case 'medications':
        return renderMedications();
      case 'motivations':
        return renderStringList(data.motivations, 'motivations', 'e.g., My kids, finishing my degree...');
      default:
        return null;
    }
  };

  // Render check-in slider
  const renderSlider = (label: string, value: number, onChange: (v: number) => void, lowLabel: string, highLabel: string) => (
    <div className="space-y-2">
      <div className="flex justify-between text-sm">
        <span>{label}</span>
        <span className="text-white/50">{value}/5</span>
      </div>
      <input
        type="range"
        min="1"
        max="5"
        value={value}
        onChange={(e) => onChange(parseInt(e.target.value))}
        className="w-full accent-accent-primary"
      />
      <div className="flex justify-between text-xs text-white/40">
        <span>{lowLabel}</span>
        <span>{highLabel}</span>
      </div>
    </div>
  );

  const totalItems = SECTIONS.reduce((sum, s) => sum + getSectionCount(s.id), 0);
  const lastCheckIn = data.recentCheckIns[0];

  return (
    <div className="p-4 space-y-4 pb-24">
      <header className="pt-2">
        <h1 className="text-2xl font-bold">Support Plan</h1>
        <p className="text-white/50 text-sm mt-1">
          Your personal wellness toolkit
        </p>
      </header>

      {/* Quick Check-in Button */}
      <button
        onClick={() => setShowCheckIn(true)}
        className="w-full card bg-accent-primary/10 border border-accent-primary/30 flex items-center justify-between"
      >
        <div className="flex items-center gap-3">
          <span className="text-2xl">📊</span>
          <div className="text-left">
            <p className="font-medium">Quick Check-in</p>
            <p className="text-xs text-white/50">
              {lastCheckIn
                ? `Last: ${new Date(lastCheckIn.timestamp).toLocaleDateString()}`
                : 'Track how you\'re feeling'}
            </p>
          </div>
        </div>
        <span className="text-accent-primary">→</span>
      </button>

      {/* Check-in Modal */}
      {showCheckIn && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
          <div className="bg-surface rounded-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="p-4 border-b border-white/10 flex items-center justify-between">
              <h2 className="text-lg font-bold">How are you feeling?</h2>
              <button
                onClick={() => setShowCheckIn(false)}
                className="text-white/50 hover:text-white p-2"
              >
                ×
              </button>
            </div>
            <div className="p-4 space-y-6">
              {renderSlider('Mood', checkInData.mood, (v) => setCheckInData(prev => ({ ...prev, mood: v })), 'Low', 'Great')}
              {renderSlider('Anxiety', checkInData.anxiety, (v) => setCheckInData(prev => ({ ...prev, anxiety: v })), 'Calm', 'Anxious')}
              {renderSlider('Energy', checkInData.energy, (v) => setCheckInData(prev => ({ ...prev, energy: v })), 'Depleted', 'Energized')}
              {renderSlider('Impulsivity', checkInData.impulsivity, (v) => setCheckInData(prev => ({ ...prev, impulsivity: v })), 'In control', 'Impulsive')}
              {renderSlider('Sleep Quality', checkInData.sleep, (v) => setCheckInData(prev => ({ ...prev, sleep: v })), 'Poor', 'Restful')}

              <div className="space-y-2">
                <label className="text-sm">Notes (optional)</label>
                <textarea
                  value={checkInData.notes}
                  onChange={(e) => setCheckInData(prev => ({ ...prev, notes: e.target.value }))}
                  placeholder="Anything else on your mind?"
                  className="w-full bg-white/10 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-accent-primary resize-none h-20"
                />
              </div>

              <button
                onClick={submitCheckIn}
                className="w-full btn-primary py-3"
              >
                Save Check-in
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Progress indicator */}
      {totalItems === 0 ? (
        <div className="card bg-white/5">
          <p className="text-sm text-center text-white/60">
            Tap any section below to build your support plan
          </p>
        </div>
      ) : (
        <div className="flex items-center gap-3 text-sm text-white/50">
          <span className="text-accent-success font-medium">{totalItems} items</span>
          <span>in your support plan</span>
        </div>
      )}

      {/* Section cards */}
      <div className="space-y-3">
        {SECTIONS.map((section) => {
          const isExpanded = expandedSection === section.id;
          const count = getSectionCount(section.id);

          return (
            <div
              key={section.id}
              className={clsx(
                'card transition-all',
                isExpanded && 'ring-2 ring-accent-primary'
              )}
            >
              <div className="flex items-start gap-3">
                <span className="text-2xl mt-0.5">{section.emoji}</span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <h3 className="font-medium">{section.title}</h3>
                    {count > 0 && (
                      <span className="text-xs bg-accent-primary/20 text-accent-primary px-2 py-0.5 rounded-full">
                        {count}
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-white/40">{section.description}</p>

                  {/* Show items preview when collapsed */}
                  {!isExpanded && renderCollapsedItems(section.id)}
                </div>
                <button
                  onClick={() => toggleSection(section.id)}
                  className={clsx(
                    'flex-shrink-0 p-2 rounded-lg transition-colors',
                    isExpanded
                      ? 'bg-accent-primary/20 text-accent-primary'
                      : 'bg-white/10 text-white/50 hover:bg-white/20 hover:text-white/70'
                  )}
                  aria-label={isExpanded ? 'Close section' : 'Edit section'}
                >
                  {isExpanded ? '✓' : '✏️'}
                </button>
              </div>

              {isExpanded && (
                <div className="mt-4 pt-4 border-t border-white/10">
                  {renderSectionContent(section.id)}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
